import React, { useState, useEffect } from 'react';
import {
  FileText,
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  X,
  Lock,
  FileSpreadsheet,
  Edit2,
  Trash2
} from 'lucide-react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  alpha,
  Stack,
  Tooltip
} from '@mui/material';
import { createEvaluationFile, updateEvaluationFile, getMyEvaluationFiles, getEmployeeEvaluationFiles } from '../services/evaluationDocumentAPIs';
import { handleLogin } from '../services/authAPIs';

const Evaluation = ({ employeeId }) => {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [evaluationFiles, setEvaluationFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);

  // Update state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [updateFile, setUpdateFile] = useState(null);
  const [updateDescription, setUpdateDescription] = useState('');

  // User role
  const [userRole, setUserRole] = useState('');

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole') || '';
    setUserRole(role);
    if (token) {
      setIsLoggedIn(true);
      setShowLogin(false);
    } else {
      setIsLoggedIn(false);
      setShowLogin(true);
    }
  }, []);

  // Fetch evaluation files if logged in
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchFiles = async () => {
      try {
        setLoading(true);
        let files;
        if (employeeId && ['HR', 'DIRECTOR'].includes(userRole)) {
          files = await getEmployeeEvaluationFiles(employeeId);
        } else {
          files = await getMyEvaluationFiles();
        }
        console.log('Fetched evaluation files:', files);
        setEvaluationFiles(Array.isArray(files) ? files : []);
      } catch (err) {
        console.error('Failed to fetch evaluation files:', err);
        setError('Failed to load your evaluation files.');
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [isLoggedIn, employeeId, userRole]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError(null);
    setLoggingIn(true);

    try {
      const response = await handleLogin({ email: loginForm.email, password: loginForm.password });
      if (response.token) {
        localStorage.setItem('userRole', response.role || ''); // Assuming response has role
        setUserRole(response.role || '');
        setIsLoggedIn(true);
        setShowLogin(false);
        setLoginForm({ email: '', password: '' });
      }
    } catch (err) {
      setLoginError(err.message || 'Invalid email or password.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    setUserRole('');
    setIsLoggedIn(false);
    setShowLogin(true);
    setEvaluationFiles([]);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        setError('Please upload an Excel file (.xlsx or .xls).');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select an Excel file to upload.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const evaluationFileData = {
        fileName: file.name,
        description: file.name.replace(/\.(xlsx|xls)$/i, ''),
      };

      if (employeeId && ['HR', 'DIRECTOR'].includes(userRole)) {
        evaluationFileData.employee = { employeeId };
      }

      const result = await createEvaluationFile(evaluationFileData, file);
      setEvaluationFiles((prev) => [...prev, result]);
      setSuccess('Evaluation file uploaded successfully!');

      setFile(null);
      const fileInput = document.getElementById('evaluation-file-input');
      if (fileInput) fileInput.value = '';
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload evaluation file.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (filePath) => {
    if (filePath) {
      window.open(filePath, '_blank');
    }
  };

  const handleOpenUpdateModal = (file) => {
    setSelectedFile(file);
    setUpdateDescription(file.description || '');
    setUpdateFile(null);
    setShowUpdateModal(true);
  };

  const handleUpdateFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        setError('Please upload an Excel file (.xlsx or .xls).');
        return;
      }
      setUpdateFile(selectedFile);
      setError(null);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const evaluationFileData = {
        fileName: updateFile ? updateFile.name : selectedFile.fileName,
        description: updateDescription,
      };

      if (employeeId && ['HR', 'DIRECTOR'].includes(userRole)) {
        evaluationFileData.employee = { employeeId };
      }

      const result = await updateEvaluationFile(selectedFile.id, evaluationFileData, updateFile);
      setEvaluationFiles((prev) =>
        prev.map((file) => (file.id === result.id ? result : file))
      );
      setSuccess('Evaluation file updated successfully!');
      handleCloseUpdateModal();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.message || 'Failed to update evaluation file.');
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setUpdateFile(null);
    setUpdateDescription('');
    setSelectedFile(null);
    setError(null);
  };

  const canModify = !employeeId || ['HR', 'DIRECTOR'].includes(userRole);

  // Login Dialog
  if (!isLoggedIn && showLogin) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'background.default',
        p: 2
      }}>
        <Paper elevation={3} sx={{
          p: 4,
          borderRadius: 3,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center'
        }}>
          <Stack spacing={3} alignItems="center">
            <Box sx={{
              p: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: '50%',
              color: 'primary.main'
            }}>
              <Lock size={32} />
            </Box>
            <Typography variant="h5" fontWeight="bold">
              Sign In to Access Evaluation
            </Typography>

            {loginError && (
              <Alert severity="error" sx={{ width: '100%', alignItems: 'center' }}>
                {loginError}
              </Alert>
            )}

            <form onSubmit={handleLoginSubmit} style={{ width: '100%' }}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                />
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loggingIn}
                  sx={{ mt: 1 }}
                >
                  {loggingIn ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Notifications */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={0} sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
              <FileSpreadsheet size={24} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              {employeeId ? 'Employee Evaluation (Excel)' : 'My Evaluation (Excel)'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleLogout}
            startIcon={<Lock size={16} />}
          >
            Logout
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {evaluationFiles.length > 0 ? (
              <Grid container spacing={2}>
                {evaluationFiles.map((file) => (
                  <Grid item xs={12} md={6} lg={4} key={file.id}>
                    <Paper variant="outlined" sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.primary.main
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 50,
                        height: 50,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main',
                        flexShrink: 0
                      }}>
                        <FileSpreadsheet size={28} />
                      </Box>

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="600" noWrap title={file.fileName} sx={{ mb: 0.5 }}>
                          {file.fileName}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Uploaded: {new Date(file.createdDate).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap title={file.description}>
                            {file.description}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Download size={14} />}
                            onClick={() => handleDownload(file.filePath)}
                            sx={{ flexGrow: 1 }}
                          >
                            Download
                          </Button>
                          {canModify && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="warning"
                                sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}
                                onClick={() => handleOpenUpdateModal(file)}
                              >
                                <Edit2 size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>

                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontStyle: 'italic', fontSize: '0.7rem' }}>
                          ⚠️ Excel files cannot be previewed in-browser.
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{
                textAlign: 'center',
                py: 6,
                bgcolor: alpha(theme.palette.action.hover, 0.5),
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <FileSpreadsheet size={48} color={theme.palette.text.disabled} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  No evaluation Excel files uploaded yet.
                </Typography>
              </Box>
            )}

            {canModify && (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Box sx={{
                  p: 3,
                  border: `2px dashed ${theme.palette.primary.main}`,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  textAlign: 'center',
                  mb: 2
                }}>
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="evaluation-file-input"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="evaluation-file-input">
                    <Button
                      variant="text"
                      component="span"
                      startIcon={<Upload size={20} />}
                      sx={{ mb: 1 }}
                    >
                      {file ? 'Change selected file' : 'Select Excel file'}
                    </Button>
                  </label>
                  {file && (
                    <Typography variant="body2" color="text.primary" fontWeight="500">
                      Selected: {file.name}
                    </Typography>
                  )}
                  {!file && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Supported formats: .xlsx, .xls
                    </Typography>
                  )}
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  disabled={!file || uploading}
                  startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <Upload size={20} />}
                >
                  {uploading ? 'Uploading...' : 'Upload Excel File'}
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </Paper>

      {/* Update Modal */}
      <Dialog open={showUpdateModal} onClose={handleCloseUpdateModal} fullWidth maxWidth="sm">
        <DialogTitle>Update Evaluation File</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              fullWidth
              value={updateDescription}
              onChange={(e) => setUpdateDescription(e.target.value)}
              placeholder="Enter description"
              required
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Replace File (Optional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  id="update-file-input"
                  type="file"
                  onChange={handleUpdateFileChange}
                />
                <label htmlFor="update-file-input">
                  <Button variant="outlined" component="span" startIcon={<Upload size={16} />}>
                    Choose File
                  </Button>
                </label>
                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                  {updateFile ? updateFile.name : 'No file selected'}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseUpdateModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            disabled={uploading}
            startIcon={uploading && <CircularProgress size={16} color="inherit" />}
          >
            {uploading ? 'Updating...' : 'Update File'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Evaluation;


